import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { Box, IconButton } from '@mui/material';
import { designSystem } from '../../design-system';

const HeaderContainer = styled(Box)(({ isOnWhiteBg }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1000,
  transition: 'all 0.3s ease',
  padding: '32px 80px',
  borderBottom: '1px solid',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',

  '@media (max-width: 1600px)': {
    padding: '28px 64px',
  },

  '@media (max-width: 1440px)': {
    padding: '24px 48px',
  },

  '@media (max-width: 1024px)': {
    padding: '20px 40px',
  },

  '@media (max-width: 768px)': {
    padding: '16px 24px',
  },

  // 白背景の上にいる時（グラスモーフィズム効果）
  ...(isOnWhiteBg && {
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderBottomColor: 'transparent',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
    '& .header-logo': {
      filter: 'brightness(0)',
      opacity: 0.7,
    },
    '& .nav-link': {
      color: 'rgba(3, 12, 30, 0.7)',
    },
    '& .hamburger-line': {
      backgroundColor: 'rgba(3, 12, 30, 0.7)',
    },
  }),

  // 黒背景の上にいる時（デフォルト）
  ...(!isOnWhiteBg && {
    backgroundColor: 'transparent',
    backdropFilter: 'none',
    borderBottomColor: 'rgba(255, 255, 255, 0.2)',
    boxShadow: 'none',
    '& .header-logo': {
      filter: 'brightness(1)',
      opacity: 1,
    },
    '& .nav-link': {
      color: designSystem.colors.lightText,
    },
    '& .hamburger-line': {
      backgroundColor: designSystem.colors.lightText,
    },
  }),
}));

const Logo = styled('img')({
  height: '28px',
  width: 'auto',
  cursor: 'pointer',
  transition: 'filter 0.3s ease',

  '@media (max-width: 1600px)': {
    height: '26px',
  },

  '@media (max-width: 1440px)': {
    height: '24px',
  },

  '@media (max-width: 1024px)': {
    height: '22px',
  },

  '@media (max-width: 768px)': {
    height: '20px',
  },
});

const Nav = styled('nav')({
  display: 'flex',
  gap: '48px',
  alignItems: 'center',

  '@media (max-width: 1600px)': {
    gap: '40px',
  },

  '@media (max-width: 1440px)': {
    gap: '36px',
  },

  '@media (max-width: 1024px)': {
    gap: '32px',
  },

  '@media (max-width: 768px)': {
    display: 'none', // モバイルではナビゲーションを非表示
  },
});

// ハンバーガー/閉じるボタン
const MenuToggleButton = styled(IconButton)({
  display: 'none',
  padding: '8px',
  zIndex: 1001,
  marginLeft: 'auto',
  
  '@media (max-width: 768px)': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'center',
    gap: '6px',
  },
});

const HamburgerLine = styled('div')(({ isOpen }) => ({
  width: '20px',
  height: '1.5px',
  transition: 'all 0.3s ease',
  position: 'relative',
  
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    width: '20px',
    height: '1.5px',
    transition: 'all 0.3s ease',
    right: 0,
  },
  
  '&::before': {
    top: isOpen ? 0 : '-6px',
    transform: isOpen ? 'rotate(45deg)' : 'rotate(0)',
  },
  
  '&::after': {
    top: isOpen ? 0 : '6px',
    transform: isOpen ? 'rotate(-45deg)' : 'rotate(0)',
    width: isOpen ? '20px' : '16px', // 閉じている時は短く
  },
  
  opacity: isOpen ? 0 : 1,
}));

// 閉じるアイコン（ヘッダー用）
const CloseIcon = styled('img')(({ isOnWhiteBg }) => ({
  width: '28px',
  height: '28px',
  filter: isOnWhiteBg ? 'brightness(0) opacity(0.7)' : 'brightness(1) opacity(1)',
  transition: 'all 0.3s ease',
  
  '&:hover': {
    transform: 'rotate(90deg)',
  },
  
  '@media (max-width: 1024px)': {
    width: '34px',
    height: '34px',
  },
  
  '@media (max-width: 768px)': {
    width: '32px',
    height: '32px',
  },
}));

// モバイルメニューのドロワー
const MobileMenu = styled(Box)(({ isOpen, isOnWhiteBg }) => ({
  position: 'fixed',
  top: 0,
  right: 0,
  width: '100%',
  maxWidth: '400px',
  height: '100vh',
  backgroundColor: isOnWhiteBg ? 'rgba(255, 255, 255, 0.95)' : 'rgba(3, 12, 30, 0.95)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
  transition: 'transform 0.3s ease',
  zIndex: 999,
  display: 'none', // PC画面では非表示
  flexDirection: 'column',
  padding: '80px 40px 40px', // 上のpaddingを調整
  gap: '32px',
  boxShadow: isOpen ? '-4px 0 24px rgba(0, 0, 0, 0.1)' : 'none',
  
  '@media (max-width: 768px)': {
    display: 'flex', // モバイルでのみ表示
  },
}));


const MobileNavLink = styled('a')(({ isOnWhiteBg }) => ({
  fontFamily: designSystem.typography.fontFamily.ja,
  fontSize: '20px',
  fontWeight: 400,
  letterSpacing: '0.5px',
  textDecoration: 'none',
  color: isOnWhiteBg ? 'rgba(3, 12, 30, 0.9)' : designSystem.colors.lightText,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  padding: '16px 0',
  borderBottom: '1px solid',
  borderBottomColor: isOnWhiteBg ? 'rgba(3, 12, 30, 0.1)' : 'rgba(255, 255, 255, 0.1)',
  
  '&:hover': {
    paddingLeft: '16px',
    opacity: 0.7,
  },
}));

// オーバーレイ（メニュー外をクリックで閉じる用）
const Overlay = styled(Box)(({ isOpen }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  opacity: isOpen ? 1 : 0,
  visibility: isOpen ? 'visible' : 'hidden',
  transition: 'all 0.3s ease',
  zIndex: 998,
  display: 'none', // PC画面では非表示
  
  '@media (max-width: 768px)': {
    display: 'block', // モバイルでのみ表示
  },
}));

const NavLink = styled('a')({
  fontFamily: designSystem.typography.fontFamily.ja,
  fontSize: '15px',
  fontWeight: 400,
  letterSpacing: '0.5px',
  textDecoration: 'none',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  position: 'relative',

  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-4px',
    left: 0,
    width: '0%',
    height: '1px',
    backgroundColor: 'currentColor',
    transition: 'width 0.3s ease',
  },

  '&:hover::after': {
    width: '100%',
  },

  '@media (max-width: 1600px)': {
    fontSize: '14px',
  },

  '@media (max-width: 1440px)': {
    fontSize: '13px',
  },

  '@media (max-width: 1024px)': {
    fontSize: '13px',
  },
  
  '@media (max-width: 768px)': {
    fontSize: '12px',
  },
});

const Header = () => {
  const [isOnWhiteBg, setIsOnWhiteBg] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // 各セクションの位置を取得
      const projectsSection = document.getElementById('projects');
      const contactSection = document.getElementById('contact');
      const scrollPosition = window.scrollY + 100; // ヘッダーの高さ分オフセット

      let onWhite = false;

      // Projectsセクション（白背景）をチェック
      if (projectsSection) {
        const projectsTop = projectsSection.offsetTop;
        const projectsBottom = projectsTop + projectsSection.offsetHeight;
        if (scrollPosition >= projectsTop && scrollPosition < projectsBottom) {
          onWhite = true;
        }
      }

      // Contactセクション（白背景）をチェック
      if (contactSection) {
        const contactTop = contactSection.offsetTop;
        const contactBottom = contactTop + contactSection.offsetHeight;
        if (scrollPosition >= contactTop && scrollPosition < contactBottom) {
          onWhite = true;
        }
      }

      setIsOnWhiteBg(onWhite);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // 初期状態をチェック

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // モバイルメニューが開いている時はスクロールを防止
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      <HeaderContainer isOnWhiteBg={isOnWhiteBg}>
        {/* Logo */}
        <Logo
          src="/stella_logo.png"
          alt="STELLA"
          onClick={scrollToTop}
          className="header-logo"
        />

        {/* Desktop Navigation */}
        <Nav>
          <NavLink className="nav-link" onClick={() => scrollToSection('about')}>
            わたしたちについて
          </NavLink>
          <NavLink className="nav-link" onClick={() => scrollToSection('projects')}>
            プロダクト
          </NavLink>
          <NavLink className="nav-link" onClick={() => scrollToSection('contact')}>
            お問い合わせ
          </NavLink>
        </Nav>

        {/* Menu Toggle Button (Mobile) */}
        <MenuToggleButton onClick={toggleMobileMenu}>
          {isMobileMenuOpen ? (
            <CloseIcon 
              src="/icon_close.png" 
              alt="閉じる" 
              isOnWhiteBg={isOnWhiteBg}
            />
          ) : (
            <HamburgerLine isOpen={false} className="hamburger-line" />
          )}
        </MenuToggleButton>
      </HeaderContainer>

      {/* Overlay */}
      <Overlay isOpen={isMobileMenuOpen} onClick={toggleMobileMenu} />

      {/* Mobile Menu */}
      <MobileMenu isOpen={isMobileMenuOpen} isOnWhiteBg={isOnWhiteBg}>
        <MobileNavLink 
          isOnWhiteBg={isOnWhiteBg} 
          onClick={() => scrollToSection('about')}
        >
          わたしたちについて
        </MobileNavLink>
        <MobileNavLink 
          isOnWhiteBg={isOnWhiteBg} 
          onClick={() => scrollToSection('projects')}
        >
          プロダクト
        </MobileNavLink>
        <MobileNavLink 
          isOnWhiteBg={isOnWhiteBg} 
          onClick={() => scrollToSection('contact')}
        >
          お問い合わせ
        </MobileNavLink>
      </MobileMenu>
    </>
  );
};

export default Header;
